import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import AddressAutocompleteInput from '../../components/form/AddressAutocompleteInput';
import Label from '../../components/form/Label';
import { Modal } from '../../components/ui/modal';
import {
  Table,
  TableBadge,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useModal } from '../../hooks/useModal';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import { formatShortDateTime } from '../../shared/time';
import { api, apiEndpoints } from '../../utils/apiService';
import { type ParsedAddress } from '../../utils/googlePlacesAddress';
import {
  AuditCallRecord,
  CALL_OUTCOME_TONES,
  CampaignCandidate,
  CompetitorAnalysisResponse,
  DripDraft,
  INSTANTLY_STATUSES,
  PROSPECT_STATUSES,
  ProspectCompetitor,
  ProspectDetail,
  ResearchNote,
  ResearchSignal,
  TEMPLATE_SOURCE_DRIP_TEMPLATE,
  contactDisplayName,
  formatDurationMs,
  formatOutcome,
  formatPercent,
  prospectStatusMeta,
  roundCoord,
} from './types';

type TabKey = 'overview' | 'calls' | 'research' | 'emails' | 'competitors';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'calls', label: 'Calls' },
  { key: 'research', label: 'Research' },
  { key: 'emails', label: 'Emails' },
  { key: 'competitors', label: 'Competitors' },
];

const isTabKey = (value: string | null): value is TabKey =>
  TABS.some((tab) => tab.key === value);

const formatDistance = (meters?: number | null): string => {
  if (meters == null) return '—';
  return meters < 1000 ? `${meters} m` : `${(meters / 1000).toFixed(1)} km`;
};

const COMPETITOR_STATUS_META: Record<
  string,
  { label: string; tone: 'success' | 'warning' | 'neutral' }
> = {
  available: { label: 'Available', tone: 'success' },
  stale: { label: 'Stale', tone: 'warning' },
  not_run: { label: 'Not run yet', tone: 'neutral' },
};

const inputClass = 'app-input h-11 w-full rounded-2xl px-4 py-2.5 text-sm';
const selectClass =
  'h-11 w-full appearance-none rounded-2xl border border-gray-300 bg-transparent px-4 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90';

const EDITABLE_FIELDS = [
  { key: 'name', label: 'Restaurant name' },
  { key: 'restaurant_group_name', label: 'Restaurant group name' },
  { key: 'phone', label: 'Phone' },
  { key: 'website', label: 'Website' },
  { key: 'contact_first_name', label: 'Contact first name' },
  { key: 'contact_last_name', label: 'Contact last name' },
  { key: 'contact_email', label: 'Contact email' },
  { key: 'contact_title', label: 'Contact title' },
  { key: 'market', label: 'Market' },
  { key: 'cuisine', label: 'Cuisine' },
  { key: 'neighborhood', label: 'Neighborhood' },
  { key: 'timezone', label: 'Timezone' },
] as const;

type EditableKey = (typeof EDITABLE_FIELDS)[number]['key'];

interface AddressForm {
  formatted_address: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
}

type EditForm = Record<EditableKey, string> & { notes: string } & AddressForm;
type DripDraftEditForm = Pick<
  DripDraft,
  'subject_1' | 'email_1' | 'email_2' | 'email_3'
>;

const buildEditForm = (prospect: ProspectDetail): EditForm => ({
  name: prospect.name ?? '',
  restaurant_group_name: prospect.restaurant_group_name ?? '',
  phone: prospect.phone ?? '',
  website: prospect.website ?? '',
  contact_first_name: prospect.contact_first_name ?? '',
  contact_last_name: prospect.contact_last_name ?? '',
  contact_email: prospect.contact_email ?? '',
  contact_title: prospect.contact_title ?? '',
  market: prospect.market ?? '',
  cuisine: prospect.cuisine ?? '',
  neighborhood: prospect.neighborhood ?? '',
  timezone: prospect.timezone ?? '',
  notes: prospect.notes ?? '',
  formatted_address: prospect.formatted_address ?? '',
  address_line_1: prospect.address_line_1 ?? '',
  address_line_2: prospect.address_line_2 ?? '',
  city: prospect.city ?? '',
  state: prospect.state ?? '',
  postal_code: prospect.postal_code ?? '',
  country: prospect.country ?? '',
  latitude: prospect.latitude != null ? Number(prospect.latitude) : null,
  longitude: prospect.longitude != null ? Number(prospect.longitude) : null,
});

const buildDripDraftEditForm = (draft: DripDraft): DripDraftEditForm => ({
  subject_1: draft.subject_1 ?? '',
  email_1: draft.email_1 ?? '',
  email_2: draft.email_2 ?? '',
  email_3: draft.email_3 ?? '',
});

const InfoCard: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="rounded-xl border border-gray-200/80 bg-gray-50/80 px-4 py-3 dark:border-white/[0.08] dark:bg-white/[0.02]">
    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
      {label}
    </p>
    <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
      {value}
    </p>
  </div>
);

const sourceTypeLabel = (type?: string) => (type || 'other').replace(/_/g, ' ');

const levelTone = (
  level?: string,
): 'success' | 'warning' | 'error' | 'neutral' => {
  switch ((level || '').toLowerCase()) {
    case 'high':
      return 'success';
    case 'medium':
      return 'warning';
    case 'low':
      return 'error';
    default:
      return 'neutral';
  }
};

const riskTone = (
  level?: string,
): 'success' | 'warning' | 'error' | 'neutral' => {
  switch ((level || '').toLowerCase()) {
    case 'high':
      return 'error';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'neutral';
  }
};

const formatMoney = (value?: number) =>
  value == null || Number.isNaN(value)
    ? '—'
    : `$${Math.round(value).toLocaleString()}`;

const formatRate = (value?: number) =>
  value == null || Number.isNaN(value) ? '—' : `${Math.round(value * 100)}%`;

const nextScheduledAuditCall = (
  calls: AuditCallRecord[],
): AuditCallRecord | null =>
  calls
    .filter((call) => call.outcome === 'scheduled' && call.scheduled_at)
    .map((call) => ({
      call,
      scheduledTime: new Date(call.scheduled_at as string).getTime(),
    }))
    .filter(({ scheduledTime }) => !Number.isNaN(scheduledTime))
    .sort((a, b) => a.scheduledTime - b.scheduledTime)[0]?.call ?? null;

const inFlightAuditCall = (calls: AuditCallRecord[]): AuditCallRecord | null =>
  [...calls]
    .filter((call) => call.outcome === 'initiated')
    .sort((a, b) => {
      const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
      const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
      return bTime - aTime;
    })[0] ?? null;

const auditCallProviderStatus = (call: AuditCallRecord): string => {
  if (call.retell_call_status) {
    return call.retell_disconnection_reason
      ? `${call.retell_call_status} (${call.retell_disconnection_reason})`
      : call.retell_call_status;
  }
  if (call.outcome === 'scheduled') return 'Queued';
  if (call.outcome === 'initiated') return 'Waiting for provider result';
  return '—';
};

const draftStatusTone = (
  status: DripDraft['status'],
): 'primary' | 'success' | 'error' | 'warning' | 'neutral' => {
  switch (status) {
    case 'approved':
      return 'primary';
    case 'pushed':
      return 'success';
    case 'draft':
      return 'warning';
    case 'rejected':
      return 'neutral';
    default:
      return 'error';
  }
};

const batchKeyForDraft = (draft: DripDraft) =>
  draft.generation_batch_id || `legacy-${draft.id}`;

// Template-rendered drafts are kept out of the AI "latest batch" grouping and
// shown in their own section below the AI variants.
const isTemplateDraft = (draft: DripDraft) =>
  draft.source_data?.template_source === TEMPLATE_SOURCE_DRIP_TEMPLATE;

interface DripTemplateOption {
  id: number;
  name: string;
  campaign_type: string | null;
  campaign_type_label: string;
  is_active: boolean;
  variants: Array<{
    label: string;
    sort_order: number;
    subject_template: string;
    email_1_template: string;
    is_active: boolean;
  }>;
}

const hasTemplateCopy = (template: DripTemplateOption) =>
  (template.variants || []).some(
    (variant) =>
      variant.is_active &&
      variant.label?.trim() &&
      variant.subject_template?.trim() &&
      variant.email_1_template?.trim(),
  );
const DRIP_GENERATION_TIMEOUT_MS = 3 * 60 * 1000;
const DRIP_EDIT_FIELDS = [
  'subject_1',
  'email_1',
  'email_2',
  'email_3',
] as const;

// Textarea that grows to fit its content so drip copy never scrolls internally.
const AutoResizeTextarea: React.FC<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
> = ({ value, className, style, ...props }) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useLayoutEffect(() => {
    resize();
  }, [value, resize]);

  useEffect(() => {
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);

  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      className={className}
      style={{ overflow: 'hidden', resize: 'none', ...style }}
      {...props}
    />
  );
};

const DripDraftCard: React.FC<{
  draft: DripDraft;
  prospectId: number;
  canApprove: boolean;
  onApprove: (draft: DripDraft) => void | Promise<void>;
  onSaveError: (message: string) => void;
  onSaved: () => void;
}> = ({ draft, prospectId, canApprove, onApprove, onSaveError, onSaved }) => {
  const canEdit = draft.status === 'draft';
  const [savedForm, setSavedForm] = useState<DripDraftEditForm>(() =>
    buildDripDraftEditForm(draft),
  );
  const [form, setForm] = useState<DripDraftEditForm>(savedForm);
  const [saving, setSaving] = useState(false);

  const isDirty = DRIP_EDIT_FIELDS.some(
    (field) => form[field] !== savedForm[field],
  );

  const handleFieldChange = (field: keyof DripDraftEditForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!isDirty) return true;
    if (saving) return false;
    setSaving(true);
    try {
      const response = await api.patch(
        apiEndpoints.updateSalesProspectDripDraft(prospectId, draft.id),
        form,
      );
      if (!response || !response.ok) {
        onSaveError(
          `Save copy failed: ${
            response ? `status ${response.status}` : 'no response from server'
          }.`,
        );
        return false;
      }
      setSavedForm(form);
      onSaved();
      return true;
    } catch (err) {
      onSaveError(
        `Save copy failed: ${
          err instanceof Error ? err.message : 'unknown error'
        }.`,
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (isDirty && !(await handleSave())) return;
    await onApprove(draft);
  };

  return (
    <div className="rounded-2xl border border-gray-200/80 p-5 dark:border-white/[0.08]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <TableBadge tone={draftStatusTone(draft.status)}>
            {draft.status}
          </TableBadge>
          {isTemplateDraft(draft) && (
            <TableBadge tone="info">Template</TableBadge>
          )}
          {draft.variant_label && (
            <TableBadge tone="info">Variant {draft.variant_label}</TableBadge>
          )}
          <span className="text-xs text-[rgb(var(--app-muted))]">
            {formatShortDateTime(draft.created_at)} · confidence{' '}
            {draft.confidence_score}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canEdit && (
            <button
              type="button"
              onClick={handleSave}
              disabled={!isDirty || saving}
              className={`app-button-secondary rounded-2xl px-4 py-2 text-sm font-medium ${
                !isDirty || saving ? 'cursor-not-allowed opacity-45' : ''
              }`}
            >
              {saving ? 'Saving…' : 'Save copy'}
            </button>
          )}
          {canApprove && draft.status === 'draft' && (
            <button
              type="button"
              onClick={handleApprove}
              disabled={saving}
              className={`rounded-2xl bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 ${
                saving ? 'cursor-not-allowed opacity-45' : ''
              }`}
            >
              {saving ? 'Saving…' : 'Select & approve'}
            </button>
          )}
        </div>
      </div>

      <div className="mt-3 rounded-xl border border-gray-200/80 bg-gray-50/60 px-4 py-3 dark:border-white/[0.08] dark:bg-white/[0.02]">
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
          Campaign strategy
        </p>
        <p className="mt-1 text-sm font-semibold text-[rgb(var(--app-text))]">
          {draft.campaign_label || draft.campaign_type || 'Campaign angle'}
        </p>
        {draft.variant_rationale && (
          <p className="mt-2 text-sm text-[rgb(var(--app-muted))]">
            {draft.variant_rationale}
          </p>
        )}
      </div>

      {draft.status === 'failed' && draft.error && (
        <p className="mt-3 text-sm text-error-600 dark:text-error-300">
          Generation failed: {draft.error}
        </p>
      )}

      {canEdit ? (
        <div className="mt-4 space-y-3">
          <div>
            <Label htmlFor={`drip-${draft.id}-subject`}>Subject</Label>
            <input
              id={`drip-${draft.id}-subject`}
              type="text"
              value={form.subject_1}
              onChange={(e) => handleFieldChange('subject_1', e.target.value)}
              className={inputClass}
            />
          </div>
          {(['email_1', 'email_2', 'email_3'] as const).map((field, idx) => (
            <div key={field}>
              <Label htmlFor={`drip-${draft.id}-${field}`}>
                Email {idx + 1}
              </Label>
              <AutoResizeTextarea
                id={`drip-${draft.id}-${field}`}
                value={form[field]}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                className="app-input w-full rounded-2xl px-4 py-2.5 text-sm"
              />
            </div>
          ))}
          {isDirty && (
            <p className="text-xs text-[rgb(var(--app-muted))]">
              Save copy before selecting this variant for approval.
            </p>
          )}
        </div>
      ) : (
        <>
          <p className="mt-3 text-sm font-semibold text-[rgb(var(--app-text))]">
            Subject: {draft.subject_1 || '—'}
          </p>

          <div className="mt-3 space-y-3">
            {[draft.email_1, draft.email_2, draft.email_3]
              .filter(Boolean)
              .map((email, idx) => (
                <div
                  key={idx}
                  className="rounded-xl border border-gray-200/80 bg-gray-50/60 px-4 py-3 dark:border-white/[0.08] dark:bg-white/[0.02]"
                >
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
                    Email {idx + 1}
                  </p>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200">
                    {email}
                  </p>
                </div>
              ))}
          </div>
        </>
      )}

      {draft.risk_notes && (
        <p className="mt-3 text-sm text-warning-700 dark:text-warning-300">
          Risk notes: {draft.risk_notes}
        </p>
      )}
    </div>
  );
};

const ProspectDetailPage: React.FC = () => {
  const { prospectId } = useParams<{ prospectId: string }>();
  const queryClient = useQueryClient();
  // Tab state lives in the URL (?tab=competitors) so copied links open
  // directly on the right tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabKey = isTabKey(tabParam) ? tabParam : 'overview';
  const setActiveTab = (tab: TabKey) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'overview') {
          next.delete('tab');
        } else {
          next.set('tab', tab);
        }
        return next;
      },
      { replace: true },
    );
  };
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [instantlyStatus, setInstantlyStatus] = useState('');
  const [instantlyNotes, setInstantlyNotes] = useState('');
  const [selectedCall, setSelectedCall] = useState<AuditCallRecord | null>(
    null,
  );
  const [autoGenerateDripAfterResearch, setAutoGenerateDripAfterResearch] =
    useState(true);
  const [dripGenerationInFlight, setDripGenerationInFlight] = useState(false);
  const [dripDraftIdsBeforeGeneration, setDripDraftIdsBeforeGeneration] =
    useState<number[] | null>(null);
  const [dripGenerationStartedAt, setDripGenerationStartedAt] = useState<
    number | null
  >(null);
  // Set when "Run research" is launched with auto-drip. Keeps drip in-flight
  // tracking active through research so polling continues afterwards, while
  // deferring the drip timeout clock until research actually finishes.
  const [autoDripAfterResearchPending, setAutoDripAfterResearchPending] =
    useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateRenderInFlight, setTemplateRenderInFlight] = useState(false);
  const transcriptModal = useModal();

  const queryKey = useScopedQueryKey('sales-prospect-detail', prospectId ?? '');

  const {
    data: prospect,
    isPending,
    isError,
    error,
    refetch,
  } = useQuery<ProspectDetail>({
    queryKey,
    enabled: Boolean(prospectId),
    queryFn: async () => {
      const response = await api.get(
        apiEndpoints.getSalesProspect(prospectId!),
      );
      if (!response || !response.ok) {
        throw new Error(
          `Failed to load prospect${response ? ` (status ${response.status})` : ''}.`,
        );
      }
      return response.json();
    },
    refetchInterval: (query) => {
      const detail = query.state.data;
      const researchInFlight = detail?.research_notes?.some(
        (n) => n.status === 'pending' || n.status === 'running',
      );
      const auditCallInFlight = detail?.audit_calls?.some(
        (call) => call.outcome === 'initiated',
      );
      const dripTimedOut =
        dripGenerationStartedAt != null &&
        Date.now() - dripGenerationStartedAt > DRIP_GENERATION_TIMEOUT_MS;
      return researchInFlight ||
        auditCallInFlight ||
        (dripGenerationInFlight && !dripTimedOut)
        ? 5000
        : false;
    },
  });

  const competitorsQueryKey = useScopedQueryKey(
    'sales-prospect-competitors',
    prospectId ?? '',
  );
  const [competitorDiscoveryQueued, setCompetitorDiscoveryQueued] =
    useState(false);
  const {
    data: competitorAnalysis,
    isPending: competitorsPending,
    isError: competitorsError,
    refetch: refetchCompetitors,
  } = useQuery<CompetitorAnalysisResponse>({
    queryKey: competitorsQueryKey,
    enabled: Boolean(prospectId) && activeTab === 'competitors',
    queryFn: async () => {
      const response = await api.get(
        apiEndpoints.getSalesProspectCompetitors(prospectId!),
      );
      if (!response || !response.ok) {
        throw new Error(
          `Failed to load competitor analysis${response ? ` (status ${response.status})` : ''}.`,
        );
      }
      return response.json();
    },
    refetchInterval: competitorDiscoveryQueued ? 5000 : false,
  });

  useEffect(() => {
    if (
      competitorDiscoveryQueued &&
      competitorAnalysis?.status === 'available'
    ) {
      setCompetitorDiscoveryQueued(false);
    }
  }, [competitorDiscoveryQueued, competitorAnalysis?.status]);

  const dripTemplatesQueryKey = useScopedQueryKey('sales-drip-templates', '');
  const { data: dripTemplateList } = useQuery<DripTemplateOption[]>({
    queryKey: dripTemplatesQueryKey,
    enabled: activeTab === 'emails',
    queryFn: async () => {
      const response = await api.get(apiEndpoints.salesConfigDripTemplates());
      if (!response || !response.ok) {
        throw new Error(
          `Failed to load drip templates${response ? ` (status ${response.status})` : ''}.`,
        );
      }
      return response.json();
    },
  });
  const dripTemplateOptions = useMemo(
    () =>
      (dripTemplateList || []).filter(
        (template) => template.is_active && hasTemplateCopy(template),
      ),
    [dripTemplateList],
  );

  const handleDiscoverCompetitors = async (force = false) => {
    if (!prospectId) return;
    setActionMessage(null);
    try {
      const response = await api.post(
        apiEndpoints.discoverSalesProspectCompetitors(prospectId),
        force ? { force: true } : {},
      );
      if (!response) {
        setActionMessage(
          'Discover competitors failed: no response from server.',
        );
        return;
      }
      const body = await response.json().catch(() => ({}));
      if (!response.ok && response.status !== 202) {
        setActionMessage(
          `Discover competitors failed: ${body.error || `status ${response.status}`}`,
        );
        return;
      }
      if (body.queued) {
        setCompetitorDiscoveryQueued(true);
        setActionMessage(
          'Competitor discovery queued. Results appear here when it finishes.',
        );
      } else {
        setActionMessage(
          'Competitor analysis is already fresh. Use "Re-run discovery" to force a refresh.',
        );
      }
      refetchCompetitors();
    } catch (err) {
      setActionMessage(
        `Discover competitors failed: ${err instanceof Error ? err.message : 'unknown error'}`,
      );
    }
  };

  useEffect(() => {
    if (prospect && editForm === null) {
      setEditForm(buildEditForm(prospect));
    }
  }, [prospect, editForm]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['sales-prospects'] });
    refetch();
  };

  const runAction = async (
    label: string,
    request: () => Promise<Response | null>,
  ): Promise<boolean> => {
    setActionMessage(null);
    try {
      const response = await request();
      if (!response) {
        setActionMessage(`${label} failed: no response from server.`);
        return false;
      }
      let body: Record<string, unknown> = {};
      try {
        body = await response.json();
      } catch {
        // Non-JSON response body.
      }
      if (!response.ok && response.status !== 202) {
        setActionMessage(
          `${label} failed: ${(body as { error?: string }).error || `status ${response.status}`}`,
        );
        return false;
      }
      setActionMessage(`${label} succeeded.`);
      invalidate();
      return true;
    } catch (err) {
      setActionMessage(
        `${label} failed: ${err instanceof Error ? err.message : 'unknown error'}`,
      );
      return false;
    }
  };

  const handleSave = async () => {
    if (!editForm || !prospectId) return;
    setSaving(true);
    setActionMessage(null);
    try {
      const response = await api.patch(
        apiEndpoints.updateSalesProspect(prospectId),
        editForm,
      );
      if (!response) {
        setActionMessage('Save failed: no response from server.');
        return;
      }
      const body = await response.json();
      if (response.status === 409) {
        setActionMessage(
          `Save failed: another active prospect already uses this ${
            body.field === 'phone' ? 'phone' : 'email'
          }${body.existing?.id ? ` (prospect #${body.existing.id})` : ''}.`,
        );
        return;
      }
      if (!response.ok) {
        setActionMessage(
          `Save failed: ${JSON.stringify(body).substring(0, 200)}`,
        );
        return;
      }
      setActionMessage('Changes saved.');
      setEditForm(null);
      invalidate();
    } catch (err) {
      setActionMessage(
        `Save failed: ${err instanceof Error ? err.message : 'unknown error'}`,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSuppressToggle = async () => {
    if (!prospect || !prospectId) return;
    const suppressing = !prospect.do_not_contact;
    let reason = '';
    if (suppressing) {
      reason = window.prompt('Suppression reason (optional):') ?? '';
    }
    await runAction(suppressing ? 'Suppress' : 'Unsuppress', () =>
      api.patch(apiEndpoints.updateSalesProspect(prospectId), {
        do_not_contact: suppressing,
        ...(suppressing ? { suppression_reason: reason } : {}),
      }),
    );
    setEditForm(null);
  };

  const handleStatusChange = async (status: string) => {
    if (!prospectId || !status) return;
    await runAction('Status update', () =>
      api.patch(apiEndpoints.updateSalesProspect(prospectId), { status }),
    );
  };

  const researchInFlight = useMemo(
    () =>
      prospect?.research_notes?.some(
        (n) => n.status === 'pending' || n.status === 'running',
      ) ?? false,
    [prospect],
  );

  const latestApprovedDraft = prospect?.drip_drafts?.find(
    (d) => d.status === 'approved',
  );

  const dripBatches = useMemo(() => {
    const groups = new Map<string, DripDraft[]>();
    (prospect?.drip_drafts || [])
      .filter((draft) => !isTemplateDraft(draft))
      .forEach((draft) => {
        const key = batchKeyForDraft(draft);
        groups.set(key, [...(groups.get(key) || []), draft]);
      });
    return Array.from(groups.entries())
      .map(([batchId, drafts]) => ({
        batchId,
        drafts: drafts.sort(
          (a, b) =>
            (a.variant_label || '').localeCompare(b.variant_label || '') ||
            a.id - b.id,
        ),
        createdAt:
          drafts.reduce(
            (latest, draft) =>
              Math.max(latest, new Date(draft.created_at).getTime()),
            0,
          ) || 0,
      }))
      .sort((a, b) => b.createdAt - a.createdAt);
  }, [prospect?.drip_drafts]);
  const latestDripBatch = dripBatches[0];
  const templateDraftBatches = useMemo(() => {
    const groups = new Map<string, DripDraft[]>();
    (prospect?.drip_drafts || []).filter(isTemplateDraft).forEach((draft) => {
      const key = batchKeyForDraft(draft);
      groups.set(key, [...(groups.get(key) || []), draft]);
    });
    return Array.from(groups.entries())
      .map(([batchId, drafts]) => ({
        batchId,
        drafts: drafts.sort(
          (a, b) =>
            (a.variant_label || '').localeCompare(b.variant_label || '') ||
            a.id - b.id,
        ),
        createdAt:
          drafts.reduce(
            (latest, draft) =>
              Math.max(latest, new Date(draft.created_at).getTime()),
            0,
          ) || 0,
      }))
      .sort((a, b) => b.createdAt - a.createdAt);
  }, [prospect?.drip_drafts]);
  const latestTemplateBatch = templateDraftBatches[0];
  const latestTemplateWarnings = useMemo(
    () =>
      Array.from(
        new Set(
          (latestTemplateBatch?.drafts || []).flatMap(
            (draft) => draft.source_data?.template_warnings || [],
          ),
        ),
      ),
    [latestTemplateBatch],
  );
  const visibleDripDraftCount =
    (latestDripBatch?.drafts.length ?? 0) +
    (latestTemplateBatch?.drafts.length ?? 0);

  const resetDripGenerationTracking = useCallback(() => {
    setDripGenerationInFlight(false);
    setDripDraftIdsBeforeGeneration(null);
    setDripGenerationStartedAt(null);
    setAutoDripAfterResearchPending(false);
  }, []);

  useEffect(() => {
    const hasNewGeneratedDraft =
      dripDraftIdsBeforeGeneration != null &&
      (prospect?.drip_drafts || []).some(
        (draft) =>
          !isTemplateDraft(draft) &&
          !dripDraftIdsBeforeGeneration.includes(draft.id),
      );
    if (dripGenerationInFlight && hasNewGeneratedDraft) {
      resetDripGenerationTracking();
    }
  }, [
    dripDraftIdsBeforeGeneration,
    dripGenerationInFlight,
    prospect?.drip_drafts,
    resetDripGenerationTracking,
  ]);

  useEffect(() => {
    if (!dripGenerationInFlight || dripGenerationStartedAt == null) return;
    const remaining = Math.max(
      0,
      DRIP_GENERATION_TIMEOUT_MS - (Date.now() - dripGenerationStartedAt),
    );
    const timeout = window.setTimeout(() => {
      resetDripGenerationTracking();
      setActionMessage(
        'Generate drip is taking longer than expected. Refresh or try again; if no variants appear, check the task logs.',
      );
    }, remaining);
    return () => window.clearTimeout(timeout);
  }, [
    dripGenerationInFlight,
    dripGenerationStartedAt,
    resetDripGenerationTracking,
  ]);

  // When auto-drip was requested with research, only start the drip timeout
  // clock once research has actually finished (true -> false transition). The
  // ref guard avoids starting it prematurely before the refetch registers the
  // new pending research note.
  const prevResearchInFlightRef = useRef(false);
  useEffect(() => {
    const wasResearchInFlight = prevResearchInFlightRef.current;
    prevResearchInFlightRef.current = researchInFlight;
    if (
      autoDripAfterResearchPending &&
      wasResearchInFlight &&
      !researchInFlight
    ) {
      setDripGenerationStartedAt(Date.now());
      setAutoDripAfterResearchPending(false);
    }
  }, [autoDripAfterResearchPending, researchInFlight]);

  const handleRunResearch = async () => {
    if (!prospect) return;
    const autoDrip = autoGenerateDripAfterResearch;
    if (autoDrip) {
      // Mirror the Generate drip in-flight tracking so polling keeps running
      // after research finishes and the async drip draft is detected. The
      // timeout clock stays deferred (started null) until research completes.
      setDripGenerationInFlight(true);
      setDripDraftIdsBeforeGeneration(
        prospect.drip_drafts
          .filter((draft) => !isTemplateDraft(draft))
          .map((draft) => draft.id),
      );
      setDripGenerationStartedAt(null);
      setAutoDripAfterResearchPending(true);
    }
    const started = await runAction('Run research', () =>
      api.post(apiEndpoints.researchSalesProspect(prospect.id), {
        auto_generate_drip: autoDrip,
      }),
    );
    if (autoDrip && !started) {
      resetDripGenerationTracking();
    }
  };

  const handleGenerateDrip = async () => {
    if (!prospect) return;
    setAutoDripAfterResearchPending(false);
    setDripGenerationInFlight(true);
    setDripDraftIdsBeforeGeneration(
      prospect.drip_drafts
        .filter((draft) => !isTemplateDraft(draft))
        .map((draft) => draft.id),
    );
    setDripGenerationStartedAt(Date.now());
    const generationStarted = await runAction('Generate drip', () =>
      api.post(apiEndpoints.generateSalesProspectDrip(prospect.id)),
    );
    if (!generationStarted) {
      resetDripGenerationTracking();
    }
  };

  const handleApproveDripDraft = async (draft: DripDraft) => {
    if (!prospect) return;
    await runAction('Select & approve draft', () =>
      api.post(apiEndpoints.approveSalesProspectDrip(prospect.id), {
        draft_id: draft.id,
      }),
    );
  };

  // Selecting a template in the dropdown immediately renders it as a draft.
  const handleTemplateSelect = async (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!prospect || !templateId || templateRenderInFlight) return;
    setTemplateRenderInFlight(true);
    try {
      await runAction('Render drip template', () =>
        api.post(apiEndpoints.renderSalesProspectDripTemplate(prospect.id), {
          template_id: Number(templateId),
        }),
      );
    } finally {
      setTemplateRenderInFlight(false);
    }
  };

  const openTranscript = (call: AuditCallRecord) => {
    setSelectedCall(call);
    transcriptModal.openModal();
  };

  if (isPending) {
    return (
      <div className="flex min-h-[300px] items-center justify-center text-sm text-[rgb(var(--app-muted))]">
        Loading prospect…
      </div>
    );
  }

  if (isError || !prospect) {
    return (
      <div className="space-y-4">
        <div className="rounded-2xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-600 dark:border-error-800/70 dark:bg-error-900/20 dark:text-error-300">
          {error instanceof Error
            ? error.message
            : 'Could not load this prospect.'}
        </div>
        <Link
          to="/sales/prospects"
          className="app-button-secondary inline-block rounded-2xl px-4 py-2 text-sm font-medium"
        >
          Back to prospects
        </Link>
      </div>
    );
  }

  const statusMeta = prospectStatusMeta(prospect.status);
  const metric = prospect.latest_metric;
  const canGenerateDrip = !prospect.do_not_contact;
  const nextCall = nextScheduledAuditCall(prospect.audit_calls);
  const waitingCall = nextCall ? null : inFlightAuditCall(prospect.audit_calls);
  const nextCallDisplay = nextCall?.scheduled_at
    ? formatShortDateTime(nextCall.scheduled_at)
    : waitingCall
      ? 'Waiting on provider…'
      : '—';

  return (
    <>
      <PageMeta
        title={`AskArthur | ${prospect.name}`}
        description="Sales prospect detail"
      />

      <div className="space-y-5">
        <div>
          <Link
            to="/sales/prospects"
            className="text-sm font-medium text-[rgb(var(--app-muted))] hover:text-[rgb(var(--app-brand-strong))]"
          >
            ← Back to prospects
          </Link>
        </div>

        <div className="app-panel rounded-[26px] p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="app-page-title">{prospect.name}</h1>
                <TableBadge tone={statusMeta.tone}>
                  {statusMeta.label}
                </TableBadge>
                {prospect.do_not_contact && (
                  <TableBadge tone="error">Suppressed</TableBadge>
                )}
              </div>
              <p className="app-page-subtitle mt-1.5">
                {[
                  prospect.restaurant_group_name,
                  contactDisplayName(prospect),
                  prospect.contact_email,
                  prospect.phone,
                  prospect.formatted_address ||
                    prospect.city ||
                    prospect.market,
                ]
                  .filter(Boolean)
                  .join(' · ') || 'No contact details yet'}
              </p>
              {prospect.do_not_contact && (
                <p className="mt-1 text-sm text-error-500">
                  Do not contact
                  {prospect.suppression_reason
                    ? `: ${prospect.suppression_reason}`
                    : ''}
                  {prospect.suppressed_at
                    ? ` (since ${formatShortDateTime(prospect.suppressed_at)})`
                    : ''}
                </p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  runAction('Start audit', () =>
                    api.post(apiEndpoints.startSalesProspectAudit(prospect.id)),
                  )
                }
                disabled={prospect.do_not_contact || !prospect.phone}
                className={`app-button-secondary rounded-2xl px-4 py-2 text-sm font-medium ${
                  prospect.do_not_contact || !prospect.phone
                    ? 'cursor-not-allowed opacity-45'
                    : ''
                }`}
                title={
                  prospect.do_not_contact
                    ? 'Prospect is suppressed'
                    : !prospect.phone
                      ? 'Prospect has no phone'
                      : 'Queue an audit call for today'
                }
              >
                Start audit
              </button>
              <button
                type="button"
                onClick={handleRunResearch}
                disabled={researchInFlight}
                className={`app-button-secondary rounded-2xl px-4 py-2 text-sm font-medium ${
                  researchInFlight ? 'cursor-not-allowed opacity-45' : ''
                }`}
                title={
                  researchInFlight
                    ? 'Research is already running'
                    : 'Look up this lead online (reviews, Reddit, news)'
                }
              >
                {researchInFlight ? 'Researching…' : 'Run research'}
              </button>
              <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={autoGenerateDripAfterResearch}
                  onChange={(event) =>
                    setAutoGenerateDripAfterResearch(event.target.checked)
                  }
                  disabled={researchInFlight}
                  className="h-4 w-4 rounded border-gray-300"
                />
                Auto-generate drip
              </label>
              <button
                type="button"
                onClick={handleGenerateDrip}
                disabled={!canGenerateDrip || dripGenerationInFlight}
                className={`app-button-secondary rounded-2xl px-4 py-2 text-sm font-medium ${
                  !canGenerateDrip || dripGenerationInFlight
                    ? 'cursor-not-allowed opacity-45'
                    : ''
                }`}
                title={
                  dripGenerationInFlight
                    ? 'Generating drip variants'
                    : canGenerateDrip
                      ? 'Generate drip email draft variants'
                      : 'Prospect is suppressed'
                }
              >
                {dripGenerationInFlight ? 'Generating…' : 'Generate drip'}
              </button>
              <button
                type="button"
                onClick={() =>
                  runAction('Push to Instantly', () =>
                    api.post(
                      apiEndpoints.pushSalesProspectToInstantly(prospect.id),
                      {
                        draft_id: latestApprovedDraft?.id,
                      },
                    ),
                  )
                }
                disabled={prospect.do_not_contact || !latestApprovedDraft}
                className={`app-button-secondary rounded-2xl px-4 py-2 text-sm font-medium ${
                  prospect.do_not_contact || !latestApprovedDraft
                    ? 'cursor-not-allowed opacity-45'
                    : ''
                }`}
                title={
                  prospect.do_not_contact
                    ? 'Prospect is suppressed'
                    : !latestApprovedDraft
                      ? 'Approve a drip draft first'
                      : 'Push the approved draft to Instantly'
                }
              >
                Push to Instantly
              </button>
              <button
                type="button"
                onClick={handleSuppressToggle}
                className={`rounded-2xl px-4 py-2 text-sm font-medium ${
                  prospect.do_not_contact
                    ? 'app-button-secondary'
                    : 'bg-error-50 text-error-600 hover:bg-error-100 dark:bg-error-900/20 dark:text-error-300'
                }`}
              >
                {prospect.do_not_contact ? 'Unsuppress' : 'Suppress'}
              </button>
            </div>
          </div>

          {actionMessage && (
            <div className="mt-4 rounded-2xl border border-[rgb(var(--app-border)/0.6)] bg-[rgb(var(--app-surface-muted)/0.8)] px-4 py-3 text-sm text-[rgb(var(--app-text))]">
              {actionMessage}
            </div>
          )}

          <div className="mt-6 flex gap-1 border-b border-[rgb(var(--app-border)/0.5)]">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2.5 text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'border-b-2 border-brand-500 text-[rgb(var(--app-brand-strong))]'
                    : 'text-[rgb(var(--app-muted))] hover:text-[rgb(var(--app-text))]'
                }`}
              >
                {tab.label}
                {tab.key === 'calls' && prospect.audit_calls.length > 0 && (
                  <span className="ml-1.5 text-xs">
                    ({prospect.audit_calls.length})
                  </span>
                )}
                {tab.key === 'emails' && visibleDripDraftCount > 0 && (
                  <span className="ml-1.5 text-xs">
                    ({visibleDripDraftCount})
                  </span>
                )}
                {tab.key === 'competitors' &&
                  (prospect.competitor_count ?? 0) > 0 && (
                    <span className="ml-1.5 text-xs">
                      ({prospect.competitor_count})
                    </span>
                  )}
              </button>
            ))}
          </div>

          <div className="mt-6">
            {activeTab === 'overview' && editForm && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <AddressAutocompleteInput
                      label="Address"
                      value={editForm.formatted_address}
                      onChange={(value) =>
                        setEditForm((prev) =>
                          prev
                            ? value === prev.formatted_address
                              ? prev
                              : {
                                  ...prev,
                                  formatted_address: value,
                                  address_line_1: '',
                                  address_line_2: '',
                                  city: '',
                                  state: '',
                                  postal_code: '',
                                  country: '',
                                  latitude: null,
                                  longitude: null,
                                }
                            : prev,
                        )
                      }
                      onPlaceSelect={(parsed: ParsedAddress) =>
                        setEditForm((prev) =>
                          prev
                            ? {
                                ...prev,
                                formatted_address: parsed.formattedAddress,
                                address_line_1: parsed.address_line_1,
                                address_line_2: parsed.address_line_2,
                                city: parsed.city,
                                state: parsed.state,
                                postal_code: parsed.postal_code,
                                country: parsed.country,
                                latitude: roundCoord(parsed.lat),
                                longitude: roundCoord(parsed.lng),
                                market: prev.market || parsed.city,
                              }
                            : prev,
                        )
                      }
                      placeholder="Start typing the restaurant address..."
                    />
                    {editForm.city && (
                      <p className="mt-1.5 text-xs text-[rgb(var(--app-muted))]">
                        {[editForm.city, editForm.state, editForm.postal_code]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {EDITABLE_FIELDS.map((field) => (
                    <div key={field.key}>
                      <Label htmlFor={`edit-${field.key}`}>{field.label}</Label>
                      <input
                        id={`edit-${field.key}`}
                        type="text"
                        value={editForm[field.key]}
                        onChange={(e) =>
                          setEditForm((prev) =>
                            prev
                              ? { ...prev, [field.key]: e.target.value }
                              : prev,
                          )
                        }
                        className={inputClass}
                      />
                    </div>
                  ))}
                  <div>
                    <Label htmlFor="edit-status">Pipeline status</Label>
                    <select
                      id="edit-status"
                      className={selectClass}
                      value={prospect.status}
                      onChange={(e) => handleStatusChange(e.target.value)}
                    >
                      {PROSPECT_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="edit-notes">Notes</Label>
                  <textarea
                    id="edit-notes"
                    rows={4}
                    value={editForm.notes}
                    onChange={(e) =>
                      setEditForm((prev) =>
                        prev ? { ...prev, notes: e.target.value } : prev,
                      )
                    }
                    className="app-input w-full rounded-2xl px-4 py-2.5 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className={`rounded-2xl bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 ${
                      saving ? 'cursor-not-allowed opacity-60' : ''
                    }`}
                  >
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditForm(buildEditForm(prospect))}
                    className="app-button-secondary rounded-2xl px-4 py-2 text-sm font-medium"
                  >
                    Reset
                  </button>
                </div>

                <div>
                  <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.08em] text-[rgb(var(--app-muted))]">
                    Google enrichment
                  </h3>
                  {prospect.enrichment_snapshots.length > 0 ? (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      {(() => {
                        const latest = prospect.enrichment_snapshots[0];
                        const fields = latest.extracted_fields as {
                          rating?: number;
                          user_ratings_total?: number;
                          formatted_address?: string;
                        };
                        return (
                          <>
                            <InfoCard
                              label="Provider"
                              value={sourceTypeLabel(latest.provider)}
                            />
                            <InfoCard
                              label="Rating"
                              value={
                                fields.rating != null
                                  ? `${fields.rating} ★ (${fields.user_ratings_total ?? 0} reviews)`
                                  : '—'
                              }
                            />
                            <InfoCard
                              label="Address"
                              value={fields.formatted_address || '—'}
                            />
                            <InfoCard
                              label="Fetched"
                              value={formatShortDateTime(latest.fetched_at)}
                            />
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <p className="text-sm text-[rgb(var(--app-muted))]">
                      No enrichment snapshots yet.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <InfoCard
                    label="Restaurant group"
                    value={prospect.restaurant_group_name || '—'}
                  />
                  <InfoCard label="Source" value={prospect.source || '—'} />
                  <InfoCard
                    label="Created"
                    value={formatShortDateTime(prospect.created_at)}
                  />
                  <InfoCard
                    label="Last contacted"
                    value={
                      prospect.last_contacted_at
                        ? formatShortDateTime(prospect.last_contacted_at)
                        : '—'
                    }
                  />
                  <InfoCard
                    label="Instantly lead"
                    value={prospect.instantly_lead_id || '—'}
                  />
                </div>
              </div>
            )}

            {activeTab === 'calls' && (
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                  <InfoCard
                    label="Total calls"
                    value={String(prospect.audit_calls.length)}
                  />
                  <InfoCard
                    label="Next scheduled call"
                    value={nextCallDisplay}
                  />
                  <InfoCard
                    label="Missed call rate"
                    value={
                      metric ? formatPercent(metric.missed_call_rate) : '—'
                    }
                  />
                  <InfoCard
                    label="Avg time to answer"
                    value={
                      metric?.avg_time_to_answer_ms != null
                        ? formatDurationMs(metric.avg_time_to_answer_ms)
                        : '—'
                    }
                  />
                  <InfoCard
                    label="Audit confidence"
                    value={metric ? metric.confidence_score : '—'}
                  />
                </div>

                {metric?.evidence_summary && (
                  <div className="rounded-2xl border border-gray-200/80 bg-gray-50/60 px-4 py-3 text-sm text-gray-700 dark:border-white/[0.08] dark:bg-white/[0.02] dark:text-gray-300">
                    {metric.evidence_summary}
                  </div>
                )}

                {prospect.audit_calls.length === 0 ? (
                  <p className="text-sm text-[rgb(var(--app-muted))]">
                    No audit calls yet. Use "Start audit" to queue one.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableCell isHeader>Date</TableCell>
                        <TableCell isHeader>Outcome</TableCell>
                        <TableCell isHeader>Time to answer</TableCell>
                        <TableCell isHeader>Duration</TableCell>
                        <TableCell isHeader>Provider status</TableCell>
                        <TableCell isHeader align="right">
                          Transcript
                        </TableCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {prospect.audit_calls.map((call) => (
                        <TableRow key={call.id}>
                          <TableCell className="font-medium text-[rgb(var(--app-text))]">
                            <div>{call.call_date}</div>
                            {call.outcome === 'scheduled' &&
                              call.scheduled_at && (
                                <div className="mt-1 text-xs font-normal text-[rgb(var(--app-muted))]">
                                  Scheduled{' '}
                                  {formatShortDateTime(call.scheduled_at)}
                                </div>
                              )}
                          </TableCell>
                          <TableCell>
                            <TableBadge
                              tone={
                                CALL_OUTCOME_TONES[call.outcome] ?? 'neutral'
                              }
                            >
                              {formatOutcome(call.outcome)}
                            </TableBadge>
                          </TableCell>
                          <TableCell>
                            {call.time_to_answer_ms != null
                              ? formatDurationMs(call.time_to_answer_ms)
                              : '—'}
                          </TableCell>
                          <TableCell>
                            {formatDurationMs(call.duration_ms)}
                          </TableCell>
                          <TableCell>{auditCallProviderStatus(call)}</TableCell>
                          <TableCell align="right">
                            {call.transcript || call.recording_url ? (
                              <button
                                type="button"
                                onClick={() => openTranscript(call)}
                                className="app-chip text-[11px] hover:border-[rgb(var(--app-brand)/0.24)] hover:text-[rgb(var(--app-brand-strong))]"
                              >
                                View
                              </button>
                            ) : (
                              <span className="text-[rgb(var(--app-muted)/0.8)]">
                                —
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}

            {activeTab === 'research' && (
              <div className="space-y-5">
                {prospect.research_notes.length === 0 ? (
                  <p className="text-sm text-[rgb(var(--app-muted))]">
                    No research yet. Use "Run research" to have the agent look
                    up this lead online — Google reviews, Reddit posts, news,
                    and more.
                  </p>
                ) : (
                  prospect.research_notes.map((note: ResearchNote) => (
                    <div
                      key={note.id}
                      className="rounded-2xl border border-gray-200/80 p-5 dark:border-white/[0.08]"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <TableBadge
                          tone={
                            note.status === 'complete'
                              ? 'success'
                              : note.status === 'failed'
                                ? 'error'
                                : 'info'
                          }
                        >
                          {note.status}
                        </TableBadge>
                        <span className="text-xs text-[rgb(var(--app-muted))]">
                          {formatShortDateTime(
                            note.completed_at ?? note.created_at,
                          )}
                          {note.model ? ` · ${note.model}` : ''}
                        </span>
                      </div>

                      {note.status === 'failed' && note.error && (
                        <p className="mt-3 text-sm text-error-600 dark:text-error-300">
                          {note.error}
                        </p>
                      )}

                      {(note.status === 'pending' ||
                        note.status === 'running') && (
                        <p className="mt-3 flex items-center gap-2 text-sm text-[rgb(var(--app-muted))]">
                          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[rgb(var(--app-border)/0.7)] border-t-[rgb(var(--app-brand-strong))]" />
                          Researching this lead online…
                        </p>
                      )}

                      {(note.recommended_campaign ||
                        note.signal_strength ||
                        note.needs_more_research) && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {note.recommended_campaign && (
                            <TableBadge tone="primary">
                              Campaign:{' '}
                              {sourceTypeLabel(note.recommended_campaign)}
                            </TableBadge>
                          )}
                          {note.signal_strength && (
                            <TableBadge tone={levelTone(note.signal_strength)}>
                              {note.signal_strength} signal strength
                            </TableBadge>
                          )}
                          {note.needs_more_research && (
                            <TableBadge tone="warning">
                              Needs more research
                            </TableBadge>
                          )}
                        </div>
                      )}

                      {note.revenue_model?.applicable && (
                        <div className="mt-3 rounded-xl border border-amber-200/80 bg-amber-50/60 px-4 py-3 dark:border-amber-800/60 dark:bg-amber-900/10">
                          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                            {formatMoney(
                              note.revenue_model
                                .estimated_annual_revenue_at_risk,
                            )}
                            /yr revenue at risk
                            <span className="ml-2 font-normal text-amber-700/80 dark:text-amber-300/70">
                              ({note.revenue_model.tier} estimate — modeled, not
                              audited)
                            </span>
                          </p>
                          <p className="mt-1 text-xs text-amber-700/90 dark:text-amber-300/80">
                            {note.revenue_model.missed_calls_per_month} missed
                            calls/mo ×{' '}
                            {formatRate(
                              note.revenue_model.high_value_inquiry_rate,
                            )}{' '}
                            high-value ×{' '}
                            {formatMoney(
                              note.revenue_model.average_inquiry_value,
                            )}{' '}
                            avg × {formatRate(note.revenue_model.close_rate)}{' '}
                            close × 12
                            {note.revenue_model.basis
                              ? ` · ${note.revenue_model.basis}`
                              : ''}
                          </p>
                        </div>
                      )}

                      {note.summary && (
                        <div className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-gray-800 dark:text-gray-200">
                          {note.summary}
                        </div>
                      )}

                      {(note.campaign_candidates?.length ?? 0) > 0 && (
                        <div className="mt-4">
                          <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-[rgb(var(--app-muted))]">
                            Campaign candidates
                          </h4>
                          <div className="space-y-2">
                            {note.campaign_candidates!.map(
                              (candidate: CampaignCandidate, idx: number) => {
                                const isWinner =
                                  candidate.campaign_key ===
                                  note.recommended_campaign;
                                return (
                                  <div
                                    key={idx}
                                    className={`rounded-xl border px-4 py-3 ${
                                      isWinner
                                        ? 'border-brand-300 bg-brand-50/40 dark:border-brand-700 dark:bg-brand-900/10'
                                        : 'border-gray-200/80 bg-gray-50/60 dark:border-white/[0.08] dark:bg-white/[0.02]'
                                    }`}
                                  >
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                        {sourceTypeLabel(
                                          candidate.campaign_key,
                                        )}
                                      </span>
                                      {isWinner && (
                                        <TableBadge tone="primary">
                                          Selected
                                        </TableBadge>
                                      )}
                                      {candidate.final_score != null && (
                                        <span className="text-xs text-[rgb(var(--app-muted))]">
                                          score {candidate.final_score}
                                        </span>
                                      )}
                                      <TableBadge
                                        tone={levelTone(
                                          candidate.evidence_strength,
                                        )}
                                      >
                                        evidence{' '}
                                        {candidate.evidence_strength || '—'}
                                      </TableBadge>
                                      <TableBadge
                                        tone={riskTone(candidate.claim_risk)}
                                      >
                                        claim risk {candidate.claim_risk || '—'}
                                      </TableBadge>
                                      {candidate.eligible === false && (
                                        <TableBadge tone="neutral">
                                          Ineligible
                                        </TableBadge>
                                      )}
                                    </div>
                                    {candidate.hook && (
                                      <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                                        “{candidate.hook}”
                                      </p>
                                    )}
                                    {candidate.eligible === false &&
                                      (candidate.ineligibility_reasons
                                        ?.length ?? 0) > 0 && (
                                        <p className="mt-1.5 text-xs italic text-[rgb(var(--app-muted))]">
                                          {candidate.ineligibility_reasons!.join(
                                            '; ',
                                          )}
                                        </p>
                                      )}
                                  </div>
                                );
                              },
                            )}
                          </div>
                        </div>
                      )}

                      {(note.signals?.length ?? 0) > 0 && (
                        <div className="mt-4">
                          <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-[rgb(var(--app-muted))]">
                            Sales signals
                          </h4>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {note.signals!.map(
                              (signal: ResearchSignal, idx: number) => (
                                <div
                                  key={idx}
                                  className="rounded-xl border border-gray-200/80 bg-gray-50/60 px-4 py-3 dark:border-white/[0.08] dark:bg-white/[0.02]"
                                >
                                  <div className="flex flex-wrap items-center gap-2">
                                    <TableBadge tone="info">
                                      {sourceTypeLabel(signal.signal_type)}
                                    </TableBadge>
                                    <TableBadge
                                      tone={levelTone(signal.confidence)}
                                    >
                                      {signal.confidence || '—'}
                                    </TableBadge>
                                    {signal.source_url && (
                                      <a
                                        href={signal.source_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="truncate text-xs font-medium text-brand-600 underline dark:text-brand-400"
                                      >
                                        {sourceTypeLabel(signal.source_type)}
                                      </a>
                                    )}
                                  </div>
                                  {signal.evidence_summary && (
                                    <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                                      {signal.evidence_summary}
                                    </p>
                                  )}
                                  {signal.sales_angle && (
                                    <p className="mt-1.5 text-xs italic text-[rgb(var(--app-muted))]">
                                      {signal.sales_angle}
                                    </p>
                                  )}
                                  {signal.campaign_hook && (
                                    <p className="mt-1.5 text-xs font-medium text-gray-600 dark:text-gray-400">
                                      Hook: “{signal.campaign_hook}”
                                    </p>
                                  )}
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}

                      {(note.subject_lines?.length ?? 0) > 0 && (
                        <div className="mt-4">
                          <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-[rgb(var(--app-muted))]">
                            Suggested subject lines
                          </h4>
                          <ul className="space-y-1">
                            {note.subject_lines!.map((subject, idx) => (
                              <li
                                key={idx}
                                className="text-sm text-gray-700 dark:text-gray-300"
                              >
                                · {subject}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {note.findings.length > 0 && (
                        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {note.findings.map((finding, idx) => (
                            <div
                              key={idx}
                              className="rounded-xl border border-gray-200/80 bg-gray-50/60 px-4 py-3 dark:border-white/[0.08] dark:bg-white/[0.02]"
                            >
                              <div className="flex items-center gap-2">
                                <TableBadge tone="info">
                                  {sourceTypeLabel(finding.source_type)}
                                </TableBadge>
                                {finding.url && (
                                  <a
                                    href={finding.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="truncate text-xs font-medium text-brand-600 underline dark:text-brand-400"
                                  >
                                    {finding.title || finding.url}
                                  </a>
                                )}
                              </div>
                              {finding.snippet && (
                                <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                                  {finding.snippet}
                                </p>
                              )}
                              {finding.relevance && (
                                <p className="mt-1.5 text-xs italic text-[rgb(var(--app-muted))]">
                                  {finding.relevance}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}

                {prospect.enrichment_snapshots.length > 0 && (
                  <div>
                    <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.08em] text-[rgb(var(--app-muted))]">
                      Enrichment snapshots
                    </h3>
                    <div className="space-y-2">
                      {prospect.enrichment_snapshots.map((snapshot) => (
                        <div
                          key={snapshot.id}
                          className="rounded-xl border border-gray-200/80 px-4 py-3 text-sm dark:border-white/[0.08]"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-[rgb(var(--app-text))]">
                              {sourceTypeLabel(snapshot.provider)}
                            </span>
                            <span className="text-xs text-[rgb(var(--app-muted))]">
                              {formatShortDateTime(snapshot.fetched_at)}
                            </span>
                          </div>
                          <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded-lg bg-gray-50/80 p-3 text-xs text-gray-600 dark:bg-white/[0.03] dark:text-gray-300">
                            {JSON.stringify(snapshot.extracted_fields, null, 2)}
                          </pre>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'emails' && (
              <div className="space-y-6">
                {visibleDripDraftCount === 0 && (
                  <p className="text-sm text-[rgb(var(--app-muted))]">
                    {dripGenerationInFlight
                      ? 'Generating drip variants… this can take a minute.'
                      : 'No drip drafts yet. Run an audit, then use "Generate drip" — or render a drip template below.'}
                  </p>
                )}

                {latestDripBatch && (
                  <div className="rounded-2xl border border-brand-200 bg-brand-50/40 p-5 dark:border-brand-800/40 dark:bg-brand-900/10">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <h3 className="text-base font-semibold text-[rgb(var(--app-text))]">
                          Latest drip strategy options
                        </h3>
                        <p className="mt-1 text-sm text-[rgb(var(--app-muted))]">
                          AI-generated variants. Compare the campaign angles,
                          then select the one to approve.
                        </p>
                      </div>
                      {dripGenerationInFlight && (
                        <TableBadge tone="info">Generating…</TableBadge>
                      )}
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
                      {latestDripBatch.drafts.map((draft) => (
                        <DripDraftCard
                          key={draft.id}
                          draft={draft}
                          prospectId={prospect.id}
                          canApprove={Boolean(prospect)}
                          onApprove={handleApproveDripDraft}
                          onSaveError={setActionMessage}
                          onSaved={invalidate}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="rounded-2xl border border-gray-200/80 p-5 dark:border-white/[0.08]">
                  <h3 className="text-base font-semibold text-[rgb(var(--app-text))]">
                    Drip templates
                  </h3>
                  <p className="mt-1 text-sm text-[rgb(var(--app-muted))]">
                    Pick a saved template to instantly render its email sequence
                    with this prospect&apos;s dynamic variables, then edit and
                    approve it like any other draft. Templates are built in{' '}
                    <Link
                      to="/sales/settings"
                      className="font-medium text-[rgb(var(--app-brand-strong))] hover:underline"
                    >
                      Sales settings
                    </Link>{' '}
                    under &quot;Drip campaign templates&quot;.
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <div className="w-full sm:w-72">
                      <Label htmlFor="drip-template">Template</Label>
                      <select
                        id="drip-template"
                        className={selectClass}
                        value={selectedTemplateId}
                        disabled={
                          templateRenderInFlight || prospect.do_not_contact
                        }
                        title={
                          prospect.do_not_contact
                            ? 'Prospect is suppressed'
                            : 'Selecting a template renders it for this prospect'
                        }
                        onChange={(e) => handleTemplateSelect(e.target.value)}
                      >
                        <option value="">Choose template…</option>
                        {dripTemplateOptions.map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                            {template.campaign_type
                              ? ` (${template.campaign_type_label || template.campaign_type})`
                              : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    {templateRenderInFlight && (
                      <TableBadge tone="info">Rendering…</TableBadge>
                    )}
                  </div>
                  {dripTemplateOptions.length === 0 && (
                    <p className="mt-3 text-sm text-[rgb(var(--app-muted))]">
                      No drip templates configured yet. To build one, open{' '}
                      <Link
                        to="/sales/settings"
                        className="font-medium text-[rgb(var(--app-brand-strong))] hover:underline"
                      >
                        Sales settings
                      </Link>{' '}
                      and create one under &quot;Drip campaign templates&quot;
                      with at least a subject and email 1.
                    </p>
                  )}

                  {latestTemplateBatch && (
                    <div className="mt-4 space-y-3">
                      <div>
                        <h4 className="text-sm font-semibold text-[rgb(var(--app-text))]">
                          Latest rendered template variants
                        </h4>
                        <p className="mt-1 text-xs text-[rgb(var(--app-muted))]">
                          Compare variants from the same template render, then
                          select one to approve.
                        </p>
                      </div>
                      {latestTemplateWarnings.length > 0 && (
                        <div className="rounded-xl border border-warning-300 bg-warning-50 px-4 py-3 text-sm text-warning-700 dark:border-warning-800/60 dark:bg-warning-900/20 dark:text-warning-300">
                          <p className="font-medium">
                            Some template variables rendered blank:
                          </p>
                          <ul className="mt-1 list-inside list-disc">
                            {latestTemplateWarnings.map((warning) => (
                              <li key={warning}>{warning}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                        {latestTemplateBatch.drafts.map((draft) => (
                          <DripDraftCard
                            key={draft.id}
                            draft={draft}
                            prospectId={prospect.id}
                            canApprove={Boolean(prospect)}
                            onApprove={handleApproveDripDraft}
                            onSaveError={setActionMessage}
                            onSaved={invalidate}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-gray-200/80 p-5 dark:border-white/[0.08]">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.08em] text-[rgb(var(--app-muted))]">
                    Instantly sync
                  </h3>
                  {prospect.instantly_sync ? (
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <InfoCard
                        label="Status"
                        value={formatOutcome(prospect.instantly_sync.status)}
                      />
                      <InfoCard
                        label="Lead ID"
                        value={prospect.instantly_sync.instantly_lead_id || '—'}
                      />
                      <InfoCard
                        label="Campaign"
                        value={prospect.instantly_sync.campaign_id || '—'}
                      />
                      <InfoCard
                        label="Last synced"
                        value={
                          prospect.instantly_sync.last_synced_at
                            ? formatShortDateTime(
                                prospect.instantly_sync.last_synced_at,
                              )
                            : '—'
                        }
                      />
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-[rgb(var(--app-muted))]">
                      Not pushed to Instantly yet.
                    </p>
                  )}

                  <div className="mt-4 flex flex-wrap items-end gap-3">
                    <div className="w-full sm:w-56">
                      <Label htmlFor="instantly-status">
                        Set Instantly status (manual sync)
                      </Label>
                      <select
                        id="instantly-status"
                        className={selectClass}
                        value={instantlyStatus}
                        onChange={(e) => setInstantlyStatus(e.target.value)}
                      >
                        <option value="">Choose status…</option>
                        {INSTANTLY_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {formatOutcome(s)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="w-full sm:w-72">
                      <Label htmlFor="instantly-notes">Notes (optional)</Label>
                      <input
                        id="instantly-notes"
                        type="text"
                        value={instantlyNotes}
                        onChange={(e) => setInstantlyNotes(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <button
                      type="button"
                      disabled={!instantlyStatus}
                      onClick={() =>
                        runAction('Mark Instantly status', () =>
                          api.post(
                            apiEndpoints.markSalesProspectStatus(prospect.id),
                            {
                              status: instantlyStatus,
                              notes: instantlyNotes,
                            },
                          ),
                        )
                      }
                      className={`app-button-secondary rounded-2xl px-4 py-2 text-sm font-medium ${
                        instantlyStatus ? '' : 'cursor-not-allowed opacity-45'
                      }`}
                    >
                      Apply
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-[rgb(var(--app-muted))]">
                    This updates the Instantly campaign status, separate from
                    the pipeline status on the Overview tab.
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'competitors' && (
              <div className="space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-[rgb(var(--app-text))]">
                      Nearby competitor analysis
                    </h3>
                    {competitorAnalysis && (
                      <TableBadge
                        tone={
                          COMPETITOR_STATUS_META[competitorAnalysis.status]
                            ?.tone ?? 'neutral'
                        }
                      >
                        {COMPETITOR_STATUS_META[competitorAnalysis.status]
                          ?.label ?? competitorAnalysis.status}
                      </TableBadge>
                    )}
                    {competitorDiscoveryQueued && (
                      <TableBadge tone="info">Discovering…</TableBadge>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      handleDiscoverCompetitors(
                        competitorAnalysis?.status === 'available',
                      )
                    }
                    disabled={
                      prospect.do_not_contact || competitorDiscoveryQueued
                    }
                    className={`app-button-secondary rounded-2xl px-4 py-2 text-sm font-medium ${
                      prospect.do_not_contact || competitorDiscoveryQueued
                        ? 'cursor-not-allowed opacity-45'
                        : ''
                    }`}
                    title={
                      prospect.do_not_contact
                        ? 'Prospect is suppressed'
                        : 'Find nearby competitors via Google Places'
                    }
                  >
                    {competitorAnalysis?.status === 'available'
                      ? 'Re-run discovery'
                      : 'Discover competitors'}
                  </button>
                </div>

                {competitorsPending ? (
                  <p className="text-sm text-[rgb(var(--app-muted))]">
                    Loading competitor analysis…
                  </p>
                ) : competitorsError ? (
                  <p className="text-sm text-error-600 dark:text-error-300">
                    Could not load competitor analysis. Try refreshing.
                  </p>
                ) : competitorAnalysis ? (
                  <>
                    {competitorAnalysis.status === 'not_run' && (
                      <p className="text-sm text-[rgb(var(--app-muted))]">
                        Competitor analysis is not available yet. Discovery runs
                        automatically before research, or you can run it
                        manually with “Discover competitors”. If it was skipped,
                        the prospect may be missing coordinates — set the
                        address (or run enrichment) first.
                      </p>
                    )}
                    {competitorAnalysis.status === 'stale' && (
                      <p className="rounded-2xl border border-warning-300 bg-warning-50 px-4 py-3 text-sm text-warning-700 dark:border-warning-800/60 dark:bg-warning-900/20 dark:text-warning-300">
                        This analysis is stale. Re-run discovery to refresh
                        competitor data before using it in outreach.
                      </p>
                    )}

                    {competitorAnalysis.competitors.length > 0 && (
                      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                        {competitorAnalysis.competitors.map(
                          (competitor: ProspectCompetitor) => (
                            <div
                              key={competitor.id}
                              className="rounded-2xl border border-gray-200/80 p-5 dark:border-white/[0.08]"
                            >
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <div>
                                  <p className="text-sm font-semibold text-[rgb(var(--app-text))]">
                                    {competitor.name}
                                  </p>
                                  <p className="mt-0.5 text-xs text-[rgb(var(--app-muted))]">
                                    {[
                                      formatDistance(
                                        competitor.distance_meters,
                                      ),
                                      competitor.formatted_address,
                                    ]
                                      .filter(Boolean)
                                      .join(' · ')}
                                  </p>
                                </div>
                                {competitor.match_confidence != null && (
                                  <TableBadge tone="info">
                                    {Math.round(
                                      competitor.match_confidence * 100,
                                    )}
                                    % match
                                  </TableBadge>
                                )}
                              </div>

                              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                                <InfoCard
                                  label="Rating"
                                  value={
                                    competitor.rating != null
                                      ? `${competitor.rating} (${competitor.user_ratings_total ?? 0} reviews)`
                                      : '—'
                                  }
                                />
                                <InfoCard
                                  label="Status"
                                  value={formatOutcome(
                                    competitor.business_status?.toLowerCase(),
                                  )}
                                />
                                <InfoCard
                                  label="Source"
                                  value={`${formatOutcome(competitor.source)}${
                                    competitor.last_seen_at
                                      ? ` · ${formatShortDateTime(competitor.last_seen_at)}`
                                      : ''
                                  }`}
                                />
                              </div>

                              {competitor.match_reason && (
                                <p className="mt-3 text-xs text-[rgb(var(--app-muted))]">
                                  Why it matched: {competitor.match_reason}
                                </p>
                              )}

                              <div className="mt-3 flex flex-wrap gap-3 text-sm">
                                {competitor.website && (
                                  <a
                                    href={competitor.website}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="font-medium text-[rgb(var(--app-brand-strong))] hover:underline"
                                  >
                                    Website
                                  </a>
                                )}
                                {competitor.google_maps_url && (
                                  <a
                                    href={competitor.google_maps_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="font-medium text-[rgb(var(--app-brand-strong))] hover:underline"
                                  >
                                    Google Maps
                                  </a>
                                )}
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    )}

                    <div className="rounded-2xl border border-gray-200/80 p-5 dark:border-white/[0.08]">
                      <h4 className="text-sm font-semibold uppercase tracking-[0.08em] text-[rgb(var(--app-muted))]">
                        Competitor campaign eligibility
                      </h4>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {competitorAnalysis.campaign_eligible == null ? (
                          <TableBadge tone="neutral">
                            Not evaluated yet
                          </TableBadge>
                        ) : competitorAnalysis.campaign_eligible ? (
                          <TableBadge tone="success">Eligible</TableBadge>
                        ) : (
                          <TableBadge tone="warning">Not eligible</TableBadge>
                        )}
                      </div>
                      {competitorAnalysis.evidence_gaps.length > 0 && (
                        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-[rgb(var(--app-muted))]">
                          {competitorAnalysis.evidence_gaps.map((gap, idx) => (
                            <li key={idx}>{gap}</li>
                          ))}
                        </ul>
                      )}
                      <p className="mt-3 text-xs text-[rgb(var(--app-muted))]">
                        Comparisons use Google-profile and convenience evidence
                        only. Competitor responsiveness (call-audit) data is not
                        collected pending legal/product review.
                      </p>
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={transcriptModal.isOpen}
        onClose={() => {
          setSelectedCall(null);
          transcriptModal.closeModal();
        }}
        className="max-w-2xl p-6 sm:p-8"
      >
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Audit call — {selectedCall?.call_date}
        </h2>
        {selectedCall && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <InfoCard
                label="Outcome"
                value={formatOutcome(selectedCall.outcome)}
              />
              <InfoCard
                label="Duration"
                value={formatDurationMs(selectedCall.duration_ms)}
              />
              <InfoCard
                label="Time to answer"
                value={
                  selectedCall.time_to_answer_ms != null
                    ? formatDurationMs(selectedCall.time_to_answer_ms)
                    : '—'
                }
              />
            </div>
            {selectedCall.recording_url && (
              <audio
                controls
                className="w-full"
                src={selectedCall.recording_url}
              >
                <a
                  href={selectedCall.recording_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  Recording
                </a>
              </audio>
            )}
            <div className="max-h-[50vh] overflow-auto rounded-2xl border border-gray-200/80 bg-gray-50/60 p-4 text-sm dark:border-white/[0.08] dark:bg-white/[0.02]">
              {selectedCall.transcript ? (
                <p className="whitespace-pre-wrap text-gray-800 dark:text-gray-200">
                  {selectedCall.transcript}
                </p>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">
                  Transcript not available.
                </p>
              )}
            </div>
            {selectedCall.classification_notes && (
              <p className="text-xs text-[rgb(var(--app-muted))]">
                {selectedCall.classification_notes}
              </p>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default ProspectDetailPage;
